import { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Button from "../CommonComponents/Button";
import Headline from "../MainComponents/Headline";
import {
    Card,
    Input,
    Label,
    ProgressFill,
    ProgressWrapper,
    Wrapper,
} from "../CommonComponents/DataCard";
import DatePicker from "../CommonComponents/DatePicker";
import { ROLES } from "../../services/Constants";
import { registerEmployee } from "../../services/EmployeeService";
import { getJobPositions, getDepartments, getEmploymentStatuses } from "../../services/LookupService";
import pdfLogo from "../../assets/pdfRed.png";
import PdfModal from "../BookComponents/PdfModal";
import MessageModal from "../CommonComponents/MessageModal";

const GlobalError = styled.p`
  color: red;
  text-align: center;
  margin: 0.5rem 0;
`;

const selectStyle = (hasError) => ({
    width: "100%",
    padding: "0.5rem",
    marginBottom: "1rem",
    fontSize: "1rem",
    borderRadius: "4px",
    border: hasError ? "1px solid red" : "1px solid #ccc",
});

function AddNewEmployee({ user }) {
    const [form, setForm] = useState({
        username: "",
        name: "",
        surname: "",
        oib: "",
        address: "",
        email: "",
        telephone: "",
        iban: "",
        availableVacationDays: "",
        dateOfBirth: "",
        citizenship: "",
        placeOfBirth: "",
        jobPositionId: "",
        departmentId: "",
        employmentStatusId: "",
        salary: "",
        gender: "MALE",
        role: ROLES.EMPLOYEE,
        contract: null,
    });

    const [jobPositions, setJobPositions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [employmentStatuses, setEmploymentStatuses] = useState([]);

    const [errors, setErrors] = useState({});
    const [globalError, setGlobalError] = useState(null);
    const [step, setStep] = useState(1);
    const [showPdf, setShowPdf] = useState(false);
    const [contract, setContract] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageSuccess, setMessageSuccess] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        async function loadLookups() {
            try {
                const [jp, d, es] = await Promise.all([
                    getJobPositions(), getDepartments(), getEmploymentStatuses()
                ]);
                setJobPositions(jp);
                setDepartments(d);
                setEmploymentStatuses(es);
            } catch (e) {
                console.error("Greška pri učitavanju šifarnika", e);
            }
        }
        loadLookups();
    }, []);

    const requiredStep1 = ["username", "oib"];
    const requiredStep2 = [
        "dateOfBirth",
        "placeOfBirth",
        "citizenship",
        "address",
        "email",
        "telephone",
    ];
    const requiredStep3 = [
        "jobPositionId",
        "departmentId",
        "employmentStatusId",
        "salary",
        "availableVacationDays",
        "iban",
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: false }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result.split(",")[1];
                setForm((prev) => ({ ...prev, contract: file }));
                setContract(base64String);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateStep = (currentStep) => {
        const newErrors = {};

        const check = (fields) => {
            fields.forEach((f) => {
                const value = form[f];
                if (
                    value === undefined ||
                    value === null ||
                    value.toString().trim() === ""
                ) {
                    newErrors[f] = true;
                }
            });
        };

        if (currentStep === 1) check(requiredStep1);
        if (currentStep === 2) check(requiredStep2);
        if (currentStep === 3) check(requiredStep3);

        if (Object.keys(newErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...newErrors }));
            setGlobalError("Molimo ispunite sva obvezna polja.");
            return false;
        }
        setGlobalError(null);
        return true;
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep((s) => Math.min(s + 1, 4));
        }
    };

    const prevStep = () => setStep((s) => Math.max(s - 1, 1));

    const handleSubmit = async (e) => {
        e.preventDefault();

        const ok1 = validateStep(1);
        const ok2 = validateStep(2);
        const ok3 = validateStep(3);

        if (!ok1 || !ok2 || !ok3) {
            setStep(1);
            return;
        }

        try {
            setGlobalError(null);
            await registerEmployee(form);

            setMessage("Zaposlenik je uspješno dodan!");
            setMessageSuccess(true);
            setShowMessageModal(true);

            // Reset form and redirect after showing success message
            setTimeout(() => {
                navigate("/sviZaposlenici");
            }, 1500);
        } catch (err) {
            setMessage(err.message || "Dogodila se greška pri dodavanju zaposlenika.");
            setMessageSuccess(false);
            setShowMessageModal(true);
        }
    };

    const handleCloseModal = () => {
        setShowMessageModal(false);
    };

    return (
        <Wrapper>
            <Card style={{ width: "500px" }}>
                <MessageModal
                    isOpen={showMessageModal}
                    onClose={handleCloseModal}
                    title="Registracija zaposlenika"
                    variant={messageSuccess ? "success" : "error"}
                >
                    {message}
                </MessageModal>

                <Headline size="2rem" center>
                    Registracija zaposlenika
                </Headline>

                {globalError && <GlobalError>{globalError}</GlobalError>}

                <ProgressWrapper>
                    <ProgressFill percent={(step / 4) * 100} />
                </ProgressWrapper>

                <form
                    onSubmit={handleSubmit}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    {step === 1 && (
                        <div>
                            <Input
                                name="username"
                                placeholder="Korisničko ime*"
                                value={form.username}
                                onChange={handleChange}
                                $error={!!errors.username}
                            />

                            <Input
                                name="name"
                                placeholder="Ime"
                                value={form.name}
                                onChange={handleChange}
                                $error={!!errors.name}
                            />

                            <Input
                                name="surname"
                                placeholder="Prezime"
                                value={form.surname}
                                onChange={handleChange}
                                $error={!!errors.surname}
                            />

                            <Input
                                name="oib"
                                placeholder="OIB*"
                                value={form.oib}
                                onChange={handleChange}
                                $error={!!errors.oib}
                            />

                            <div >
                                <label>
                                    <input
                                        type="radio"
                                        name="gender"
                                        value="MALE"
                                        checked={form.gender === "MALE"}
                                        onChange={handleChange}
                                    />
                                    Muški
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="gender"
                                        value="FEMALE"
                                        checked={form.gender === "FEMALE"}
                                        onChange={handleChange}
                                    />
                                    Ženski
                                </label>
                            </div>

                            <Label>Uloga:</Label>
                            <select
                                name="role"
                                value={form.role}
                                onChange={handleChange}
                                style={{
                                    width: "100%",
                                    padding: "0.5rem",
                                    marginBottom: "1rem",
                                    fontSize: "1rem",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                }}
                            >
                                <option value={ROLES.EMPLOYEE}>Zaposlenik</option>
                                <option value={ROLES.ADMIN}>Administrator</option>
                            </select>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <Label>Datum rođenja*</Label>
                            <DatePicker
                                name="dateOfBirth"
                                value={form.dateOfBirth}
                                onChange={handleChange}
                                $error={!!errors.dateOfBirth}
                                maxYear={new Date().getFullYear() - 18}
                            />

                            <Input
                                name="placeOfBirth"
                                placeholder="Mjesto rođenja*"
                                value={form.placeOfBirth}
                                onChange={handleChange}
                                $error={!!errors.placeOfBirth}
                            />

                            <Input
                                name="citizenship"
                                placeholder="Državljanstvo*"
                                value={form.citizenship}
                                onChange={handleChange}
                                $error={!!errors.citizenship}
                            />

                            <Input
                                name="address"
                                placeholder="Adresa*"
                                value={form.address}
                                onChange={handleChange}
                                $error={!!errors.address}
                            />

                            <Input
                                name="email"
                                placeholder="Email*"
                                value={form.email}
                                onChange={handleChange}
                                $error={!!errors.email}
                            />

                            <Input
                                name="telephone"
                                placeholder="Telefon*"
                                value={form.telephone}
                                onChange={handleChange}
                                $error={!!errors.telephone}
                            />
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <Label>Radna pozicija*</Label>
                            <select
                                name="jobPositionId"
                                value={form.jobPositionId}
                                onChange={handleChange}
                                style={selectStyle(!!errors.jobPositionId)}
                            >
                                <option value="">Odaberite poziciju...</option>
                                {jobPositions.map(jp => (
                                    <option key={jp.id} value={jp.id}>{jp.name}</option>
                                ))}
                            </select>

                            <Label>Odjel*</Label>
                            <select
                                name="departmentId"
                                value={form.departmentId}
                                onChange={handleChange}
                                style={selectStyle(!!errors.departmentId)}
                            >
                                <option value="">Odaberite odjel...</option>
                                {departments.map(d => (
                                    <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                            </select>

                            <Label>Status ugovora*</Label>
                            <select
                                name="employmentStatusId"
                                value={form.employmentStatusId}
                                onChange={handleChange}
                                style={selectStyle(!!errors.employmentStatusId)}
                            >
                                <option value="">Odaberite status ugovora...</option>
                                {employmentStatuses.map(es => (
                                    <option key={es.id} value={es.id}>{es.name}</option>
                                ))}
                            </select>

                            <Input
                                name="salary"
                                placeholder="Plaća*"
                                value={form.salary}
                                onChange={handleChange}
                                $error={!!errors.salary}
                            />

                            <Input
                                name="availableVacationDays"
                                placeholder="Dostupni dani godišnjeg*"
                                value={form.availableVacationDays}
                                onChange={handleChange}
                                $error={!!errors.availableVacationDays}
                            />

                            <Input
                                name="iban"
                                placeholder="IBAN*"
                                value={form.iban}
                                onChange={handleChange}
                                $error={!!errors.iban}
                            />

                            <div
                                style={{ display: "flex", alignItems: "center", gap: "10px" }}
                            >
                                <Label htmlFor="contract">Ugovor o radu:</Label>
                                <Input
                                    id="contract"
                                    type="file"
                                    accept="application/pdf"
                                    onChange={handleFileChange}
                                />
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div
                            style={{
                                padding: "10px",
                                background: "#f5f5f5",
                                borderRadius: "8px",
                            }}
                        >
                            <h4 style={{ marginBottom: "10px" }}>Pregled podataka</h4>
                            <ul style={{ listStyle: "none", padding: 0 }}>
                                <li>
                                    <strong>Korisničko ime:</strong> {form.username}
                                </li>
                                <li>
                                    <strong>Ime:</strong> {form.name}
                                </li>
                                <li>
                                    <strong>Prezime:</strong> {form.surname}
                                </li>
                                <li>
                                    <strong>OIB:</strong> {form.oib}
                                </li>
                                <li>
                                    <strong>Adresa:</strong> {form.address}
                                </li>
                                <li>
                                    <strong>Email:</strong> {form.email}
                                </li>
                                <li>
                                    <strong>Telefon:</strong> {form.telephone}
                                </li>
                                <li>
                                    <strong>Spol:</strong> {form.gender === "MALE" ? "Muški" : "Ženski"}
                                </li>
                                <li>
                                    <strong>Uloga:</strong> {form.role === ROLES.ADMIN ? "Administrator" : "Zaposlenik"}
                                </li>
                                <li>
                                    <strong>Datum rođenja:</strong> {form.dateOfBirth}
                                </li>
                                <li>
                                    <strong>Mjesto rođenja:</strong> {form.placeOfBirth}
                                </li>
                                <li>
                                    <strong>Državljanstvo:</strong> {form.citizenship}
                                </li>
                                <li>
                                    <strong>IBAN:</strong> {form.iban}
                                </li>
                                <li>
                                    <strong>Pozicija:</strong> {jobPositions.find(j => j.id === Number(form.jobPositionId))?.name || "-"}
                                </li>
                                <li>
                                    <strong>Odjel:</strong> {departments.find(d => d.id === Number(form.departmentId))?.name || "-"}
                                </li>
                                <li>
                                    <strong>Status ugovora:</strong> {employmentStatuses.find(e => e.id === Number(form.employmentStatusId))?.name || "-"}
                                </li>
                                <li>
                                    <strong>Plaća:</strong> {form.salary}
                                </li>
                                <li>
                                    <strong>Dani godišnjeg:</strong>{" "}
                                    {form.availableVacationDays}
                                </li>
                                <div
                                    onClick={() => setShowPdf(true)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <img src={pdfLogo} alt="PDF" width="24" height="24" />
                                    <p>Ugovor</p>
                                </div>
                            </ul>
                        </div>
                    )}

                    <div
                        style={{
                            marginTop: "20px",
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "1rem",
                        }}
                    >
                        {step > 1 && (
                            <Button tekst="Nazad" akcija={prevStep} sirina="fit-content" />
                        )}

                        {step < 4 && (
                            <Button tekst="Dalje" akcija={nextStep} sirina="fit-content" />
                        )}

                        {step === 4 && user.role === ROLES.ADMIN && (
                            <Button
                                tekst="Dodaj zaposlenika"
                                akcija={handleSubmit}
                                sirina="fit-content"
                                type="submit"
                            />
                        )}
                    </div>
                </form>

                {showPdf && (
                    <PdfModal content={contract} onClose={() => setShowPdf(false)} />
                )}
            </Card>
        </Wrapper>
    );
}

export default AddNewEmployee;
